import * as Sentry from '@sentry/react';
import packageJson from '../../package.json';
import { User } from '../interfaces/user';

/**
 * @description Initialize Sentry error tracking and monitoring for staging and production environments
 *
 * @SADtrace SAD-003-1
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @returns {void}
 */
export const initSentry = (): void => {
  const environment = import.meta.env.VITE_APP_ENV;

  if (environment === 'staging' || environment === 'production') {
    Sentry.init({
      dsn: import.meta.env.VITE_SENTRY_DSN,
      environment: environment,
      debug: environment === 'staging',
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration(),
      ],
      tracesSampleRate: environment === 'staging' ? 1.0 : 0.1,
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 1.0,
      release: packageJson.version,
      /**
       * @description Before send hook that logs errors to console in staging environment
       *
       * @SADtrace SAD-003-1
       *
       * @History
       * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
       * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
       *
       * @param {Sentry.ErrorEvent} event - Sentry error event
       * @param {Sentry.EventHint} hint - Event hint with original exception
       *
       * @returns {Sentry.ErrorEvent} Modified or original event
       */
      beforeSend(event: Sentry.ErrorEvent, hint: Sentry.EventHint) {
        if (event.exception && environment === 'staging') {
          const error = hint.originalException;
          console.error('Sentry captured error:', error);
        }
        return event;
      },
      initialScope: {
        tags: {
          component: 'drug-library-fe',
          environment: environment,
        },
      },
    });
  }
};

/**
 * @description Set Sentry user context for error tracking and identification
 *
 * @SADtrace SAD-003-1
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @param {User} user - User object with identification details
 *
 * @returns {void}
 */
export const setSentryUser = (user: User): void => {
  Sentry.setUser({
    id: user.id.toString(),
    email: user.email,
    username: user.cognito_id,
    name: `${user.first_name} ${user.last_name}`,
    role: user.role,
    company_id: user.company_id?.toString(),
    company_name: user.company_name || undefined,
  });
};

/**
 * @description Clear Sentry user context on logout
 *
 * @SADtrace SAD-003-1
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @returns {void}
 */
export const clearSentryUser = (): void => {
  Sentry.setUser(null);
};

/**
 * @description Report a handled exception to Sentry. No-op when Sentry is not
 * initialized (e.g. local dev, where initSentry only runs for staging/production).
 *
 * @param {unknown} error - The error/exception to capture
 *
 * @returns {void}
 */
export const captureSentryException = (error: unknown): void => {
  Sentry.captureException(error);
};
